// src/pages/AffectationsBySite.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const AffectationsBySite = () => {
  const { id_site } = useParams();
  const navigate = useNavigate();
  
  const [site, setSite] = useState(null);
  const [agentsAffectes, setAgentsAffectes] = useState([]);
  const [agentsDisponibles, setAgentsDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Charger les agents affectés au site
  const fetchAgentsBySite = async () => {
    if (!userInfo || !id_site) return;
    
    setLoading(true);
    setError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Session expirée");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    try {
      console.log("🔵 Chargement des agents pour le site:", id_site);
      
      const response = await axios.get(
        `${API_BASE_URL}/affectations/agents-by-site.php?id_site=${id_site}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      console.log("🟢 Réponse agents par site:", response.data);
      
      if (response.data?.success) {
        setSite(response.data.site);
        setAgentsAffectes(response.data.agents_affectes || []);
        setAgentsDisponibles(response.data.agents_disponibles || []);
      } else {
        setError(response.data?.message || "Erreur de chargement des agents");
      }
    } catch (err) {
      console.error("❌ Erreur chargement agents par site:", err);
      console.error("❌ Détails erreur:", err.response?.data || err.message);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setError("Permission refusée. Vous n'avez pas accès à ce site.");
      } else if (err.response?.status === 404) {
        setError(err.response.data?.message || "Site non trouvé");
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo && id_site) {
      fetchAgentsBySite();
    }
  }, [userInfo, id_site]);

  // Retirer un agent du site
  const handleRemoveAgent = async (id_agent) => {
    if (!window.confirm("Êtes-vous sûr de vouloir retirer cet agent de ce site ?")) {
      return;
    }
    
    setActionLoading(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      
      const response = await axios.delete(
        `${API_BASE_URL}/affectations/delete.php`,
        {
          data: { id_site: parseInt(id_site), id_agent: parseInt(id_agent) },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Agent retiré du site avec succès !");
        // Recharger les données
        fetchAgentsBySite();
      } else {
        setError(response.data?.message || "Erreur lors du retrait de l'agent");
      }
    } catch (err) {
      console.error("❌ Erreur retrait agent:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 404) {
        setError(err.response.data?.message || "Affectation non trouvée");
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Affecter un agent disponible
  const handleAffectAgent = async (id_agent) => {
    setActionLoading(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      
      const response = await axios.post(
        `${API_BASE_URL}/affectations/create.php`,
        {
          id_site: parseInt(id_site),
          id_agent: parseInt(id_agent)
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Agent affecté au site avec succès !");
        // Recharger les données
        fetchAgentsBySite();
      } else {
        setError(response.data?.message || "Erreur lors de l'affectation");
      }
    } catch (err) {
      console.error("❌ Erreur affectation:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 409) {
        setError(err.response.data?.message || "Cet agent est déjà affecté à ce site");
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Agents du site</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/affectations">Affectations</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des agents du site...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error || !site) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Agents du site</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/affectations">Affectations</Link>
              </li>
              <li className="breadcrumb-item active">Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="alert alert-danger alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error || "Site non trouvé"}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
              <div className="text-center mt-4">
                <button 
                  className="btn fw-semibold me-3"
                  onClick={() => navigate("/affectations")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux affectations
                </button>
                <button 
                  className="btn fw-semibold"
                  onClick={() => navigate("/sites")}
                  style={{
                    color: COLORS.darkText,
                    borderColor: '#e0e0e0',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.lightBg;
                    e.target.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  <i className="bi bi-building me-2"></i>
                  Voir tous les sites
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Agents du site</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/affectations">Affectations</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>{site.nom_site}</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {/* Messages */}
        {successMsg && (
          <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#e8f5e9',
            borderColor: COLORS.success,
            color: '#2e7d32',
            borderRadius: '8px',
            borderLeft: `4px solid ${COLORS.success}`
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
              <div className="flex-grow-1">{successMsg}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSuccessMsg("")}
              ></button>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#ffebee',
            borderColor: COLORS.danger,
            color: '#c62828',
            borderRadius: '8px',
            borderLeft: `4px solid ${COLORS.danger}`
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <div className="flex-grow-1">{error}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError("")}
              ></button>
            </div>
          </div>
        )}

        {/* Informations du site */}
        <div className="card mb-4" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="card-title mb-2" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                    width: '36px',
                    height: '36px',
                    backgroundColor: COLORS.primary,
                    color: 'white'
                  }}>
                    <i className="bi bi-building"></i>
                  </div>
                  {site.nom_site}
                </h5>
                <div className="d-flex align-items-center" style={{ color: COLORS.darkText }}>
                  <i className="bi bi-geo-alt me-2" style={{ color: COLORS.secondary }}></i>
                  {site.adresse_site || "Pas d'adresse"}
                  {site.ville_site && `, ${site.ville_site}`}
                </div>
                <div className="mt-2">
                  <span className="badge me-2" style={{ 
                    backgroundColor: site.statut_site === 1 ? '#e8f5e9' : '#f5f5f5',
                    color: site.statut_site === 1 ? '#2e7d32' : '#757575',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontWeight: '500'
                  }}>
                    {site.statut_site === 1 ? 'Site Actif' : 'Site Inactif'}
                  </span>
                  <span className="badge" style={{ 
                    backgroundColor: '#E3F2FD',
                    color: COLORS.primary,
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontWeight: '500'
                  }}>
                    <i className="bi bi-people me-1"></i>
                    {agentsAffectes.length} agent(s) affecté(s)
                  </span>
                </div>
              </div>
              <div className="d-flex gap-2">
                <Link 
                  to={`/sites/details/${site.id_site}`}
                  className="btn fw-semibold"
                  target="_blank"
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-eye me-1"></i>
                  Voir le site
                </Link>
                <button 
                  className="btn fw-semibold"
                  onClick={() => navigate("/affectations")}
                  style={{
                    color: COLORS.darkText,
                    borderColor: '#e0e0e0',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.lightBg;
                    e.target.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  Retour
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Section 1: Agents affectés */}
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              marginBottom: '24px'
            }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-2" style={{ color: COLORS.primary, fontWeight: '600' }}>
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32'
                      }}>
                        <i className="bi bi-person-check"></i>
                      </div>
                      Agents affectés à ce site
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge me-2" style={{ 
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}>
                        {agentsAffectes.length} agent(s)
                      </span>
                      <span style={{ color: '#757575', fontSize: '0.875rem' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Agents actuellement en poste sur ce site
                      </span>
                    </div>
                  </div>
                  
                  {userInfo?.profile === 3 && agentsAffectes.length > 0 && (
                    <div className="alert p-3 mb-0" style={{ 
                      backgroundColor: '#E3F2FD',
                      borderColor: COLORS.accent,
                      color: COLORS.primary,
                      borderRadius: '8px'
                    }}>
                      <small>
                        <i className="bi bi-info-circle me-1"></i>
                        Cliquez sur <span className="badge" style={{ 
                          backgroundColor: COLORS.danger,
                          color: 'white',
                          padding: '2px 8px',
                          margin: '0 4px'
                        }}>✕</span> pour retirer un agent
                      </small>
                    </div>
                  )}
                </div>
                
                {agentsAffectes.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-person-x fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun agent affecté à ce site</h6>
                    <p style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      Aucun agent n'est actuellement affecté à ce site. Vous pouvez affecter des agents depuis la section "Agents disponibles" ci-dessous.
                    </p>
                    {userInfo?.profile === 3 && agentsDisponibles.length > 0 && (
                      <button 
                        className="btn fw-semibold mt-3"
                        onClick={() => document.getElementById('agents-disponibles').scrollIntoView({ behavior: 'smooth' })}
                        style={{
                          color: 'white',
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                          borderRadius: '8px',
                          padding: '10px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.secondary;
                          e.target.style.borderColor = COLORS.secondary;
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.borderColor = COLORS.primary;
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Affecter un agent
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr style={{ 
                          backgroundColor: COLORS.lightBg,
                          borderBottom: `2px solid ${COLORS.accent}`
                        }}>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Nom</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Matricule</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Téléphone</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Affecté depuis</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentsAffectes.map((agent) => (
                          <tr key={agent.id_agent} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                            <td style={{ padding: '16px' }}>
                              <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                {agent.prenom_agent} {agent.nom_agent}
                              </div>
                              {agent.email_user && (
                                <div className="small" style={{ color: '#757575' }}>
                                  <i className="bi bi-envelope me-1"></i>
                                  {agent.email_user}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '16px', color: COLORS.darkText }}>
                              {agent.matricule_agent || "-"}
                            </td>
                            <td style={{ padding: '16px', color: COLORS.darkText }}>
                              {agent.tel_agent || "-"}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <span className="badge" style={{ 
                                backgroundColor: agent.status_agent === 1 ? '#e8f5e9' : '#f5f5f5',
                                color: agent.status_agent === 1 ? '#2e7d32' : '#757575',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontWeight: '500'
                              }}>
                                {agent.status_agent === 1 ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td style={{ padding: '16px', color: COLORS.darkText }}>
                              {agent.created_at ? new Date(agent.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : "-"}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div className="d-flex gap-2">
                                {agent.id_user ? (
                                  <Link 
                                    to={`/users/details/${agent.id_user}`}
                                    className="btn fw-semibold"
                                    target="_blank"
                                    style={{
                                      color: COLORS.primary,
                                      borderColor: COLORS.primary,
                                      backgroundColor: 'transparent',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '0.85rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = COLORS.primary;
                                      e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = COLORS.primary;
                                    }}
                                  >
                                    <i className="bi bi-eye"></i>
                                  </Link>
                                ) : (
                                  <button
                                    className="btn fw-semibold"
                                    disabled
                                    title="Cet agent n'est pas lié à un utilisateur"
                                    style={{
                                      color: '#757575',
                                      borderColor: '#e0e0e0',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '0.85rem',
                                      cursor: 'not-allowed'
                                    }}
                                  >
                                    <i className="bi bi-eye-slash"></i>
                                  </button>
                                )}
                                
                                {userInfo?.profile === 3 && (
                                  <button
                                    className="btn fw-semibold"
                                    onClick={() => handleRemoveAgent(agent.id_agent)}
                                    disabled={actionLoading}
                                    title="Retirer du site"
                                    style={{
                                      color: actionLoading ? '#757575' : COLORS.danger,
                                      borderColor: actionLoading ? '#e0e0e0' : COLORS.danger,
                                      backgroundColor: 'transparent',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '0.85rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!actionLoading) {
                                        e.target.style.backgroundColor = COLORS.danger;
                                        e.target.style.color = 'white';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!actionLoading) {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.color = COLORS.danger;
                                      }
                                    }}
                                  >
                                    {actionLoading ? (
                                      <span className="spinner-border spinner-border-sm" style={{ width: '1rem', height: '1rem' }}></span>
                                    ) : (
                                      <i className="bi bi-x-lg"></i>
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Agents disponibles (uniquement pour gestionnaires) */}
        {userInfo?.profile === 3 && agentsDisponibles.length > 0 && (
          <div className="row mt-4">
            <div className="col-lg-12">
              <div className="card" id="agents-disponibles" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h5 className="card-title mb-2" style={{ color: COLORS.primary, fontWeight: '600' }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '32px',
                          height: '32px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-person-plus"></i>
                        </div>
                        Agents disponibles pour affectation
                      </h5>
                      <div className="d-flex align-items-center">
                        <span className="badge me-2" style={{ 
                          backgroundColor: COLORS.accent,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}>
                          {agentsDisponibles.length} agent(s)
                        </span>
                        <span style={{ color: '#757575', fontSize: '0.875rem' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Agents de votre société non affectés à un site
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr style={{ 
                          backgroundColor: COLORS.lightBg,
                          borderBottom: `2px solid ${COLORS.accent}`
                        }}>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Nom</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Matricule</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Téléphone</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Compte utilisateur</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentsDisponibles.map((agent) => (
                          <tr key={`dispo-${agent.id_agent}`} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                            <td style={{ padding: '16px' }}>
                              <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                {agent.prenom_agent} {agent.nom_agent}
                              </div>
                              {agent.email_user && (
                                <div className="small" style={{ color: '#757575' }}>
                                  <i className="bi bi-envelope me-1"></i>
                                  {agent.email_user}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '16px', color: COLORS.darkText }}>
                              {agent.matricule_agent || "-"}
                            </td>
                            <td style={{ padding: '16px', color: COLORS.darkText }}>
                              {agent.tel_agent || "-"}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <span className="badge" style={{ 
                                backgroundColor: agent.status_agent === 1 ? '#e8f5e9' : '#f5f5f5',
                                color: agent.status_agent === 1 ? '#2e7d32' : '#757575',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontWeight: '500'
                              }}>
                                {agent.status_agent === 1 ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td style={{ padding: '16px' }}>
                              {agent.has_user_account ? (
                                <span className="badge" style={{ 
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontWeight: '500'
                                }}>
                                  <i className="bi bi-check-circle me-1"></i>
                                  Compte lié
                                </span>
                              ) : (
                                <span className="badge" style={{ 
                                  backgroundColor: '#fff8e1',
                                  color: '#ff8f00',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontWeight: '500'
                                }}>
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  Non lié
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div className="d-flex gap-2">
                                {agent.id_user ? (
                                  <Link 
                                    to={`/users/details/${agent.id_user}`}
                                    className="btn fw-semibold"
                                    target="_blank"
                                    style={{
                                      color: COLORS.primary,
                                      borderColor: COLORS.primary,
                                      backgroundColor: 'transparent',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '0.85rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = COLORS.primary;
                                      e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = COLORS.primary;
                                    }}
                                  >
                                    <i className="bi bi-eye"></i>
                                  </Link>
                                ) : (
                                  <button
                                    className="btn fw-semibold"
                                    disabled
                                    title="Cet agent n'est pas lié à un utilisateur"
                                    style={{
                                      color: '#757575',
                                      borderColor: '#e0e0e0',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '0.85rem',
                                      cursor: 'not-allowed'
                                    }}
                                  >
                                    <i className="bi bi-eye-slash"></i>
                                  </button>
                                )}
                                
                                <button
                                  className="btn fw-semibold"
                                  onClick={() => handleAffectAgent(agent.id_agent)}
                                  disabled={actionLoading || agent.status_agent !== 1}
                                  title={agent.status_agent !== 1 ? "Agent inactif" : "Affecter au site"}
                                  style={{
                                    color: actionLoading || agent.status_agent !== 1 ? '#757575' : 'white',
                                    borderColor: actionLoading || agent.status_agent !== 1 ? '#e0e0e0' : COLORS.primary,
                                    backgroundColor: actionLoading || agent.status_agent !== 1 ? '#f5f5f5' : COLORS.primary,
                                    borderRadius: '6px',
                                    padding: '6px 16px',
                                    fontSize: '0.85rem',
                                    transition: 'all 0.3s ease',
                                    cursor: actionLoading || agent.status_agent !== 1 ? 'not-allowed' : 'pointer'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!actionLoading && agent.status_agent === 1) {
                                      e.target.style.backgroundColor = COLORS.secondary;
                                      e.target.style.borderColor = COLORS.secondary;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!actionLoading && agent.status_agent === 1) {
                                      e.target.style.backgroundColor = COLORS.primary;
                                      e.target.style.borderColor = COLORS.primary;
                                    }
                                  }}
                                >
                                  {actionLoading ? (
                                    <span className="spinner-border spinner-border-sm" style={{ width: '1rem', height: '1rem' }}></span>
                                  ) : (
                                    <>
                                      <i className="bi bi-link me-1"></i>
                                      Affecter
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {agentsDisponibles.filter(a => a.status_agent !== 1).length > 0 && (
                    <div className="alert mt-3" style={{ 
                      backgroundColor: '#fff8e1',
                      borderColor: COLORS.warning,
                      color: '#ff8f00',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${COLORS.warning}`
                    }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle me-2" style={{ color: '#ff9800' }}></i>
                        <div>
                          <small>
                            <strong>Note :</strong> {agentsDisponibles.filter(a => a.status_agent !== 1).length} agent(s) inactif(s) ne peuvent pas être affectés.
                            Activez-les d'abord depuis la gestion des agents.
                          </small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="mt-5">
          <div className="d-flex justify-content-between">
            <div className="d-flex gap-2">
              <button
                className="btn fw-semibold"
                onClick={() => navigate("/affectations")}
                style={{
                  color: COLORS.darkText,
                  borderColor: '#e0e0e0',
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.lightBg;
                  e.target.style.borderColor = COLORS.primary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#e0e0e0';
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour aux affectations
              </button>
              
              {userInfo?.profile === 3 && (
                <Link
                  to={`/affectations/create?site_id=${id_site}`}
                  className="btn fw-semibold"
                  style={{
                    color: 'white',
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                    e.target.style.borderColor = COLORS.secondary;
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.borderColor = COLORS.primary;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Nouvelle affectation
                </Link>
              )}
            </div>
            
            <div>
              <Link
                to={`/sites/details/${id_site}`}
                className="btn fw-semibold"
                target="_blank"
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#E3F2FD';
                  e.target.style.borderColor = COLORS.secondary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = COLORS.primary;
                }}
              >
                <i className="bi bi-building me-2"></i>
                Détails du site
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .alert strong,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .d-flex.align-items-center {
            flex-direction: column;
            align-items: flex-start !important;
          }
          
          .d-flex.gap-2 {
            justify-content: center;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </Layout>
  );
};

export default AffectationsBySite;